import React, { useEffect, useState } from "react";
import { animate, motion, useReducedMotion } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

function useAnimatedCount(target, { duration = 0.48 } = {}) {
  const reduced = useReducedMotion();
  const [value, setValue] = useState(() => (reduced ? target : 0));

  useEffect(() => {
    if (reduced) {
      setValue(target);
      return;
    }
    const controls = animate(0, target, {
      duration,
      ease: EASE,
      onUpdate: (latest) => setValue(Math.round(latest)),
    });
    return () => controls.stop();
  }, [target, duration, reduced]);

  return value;
}

function StatShell({ embedded, children, className = "", ...props }) {
  return (
    <motion.div
      initial={false}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.35, ease: EASE }}
      className={`neo-panel inline-flex min-h-[44px] items-center ${embedded ? "gap-1 px-4" : "gap-1.5 px-4"} ${className}`}
      {...props}
    >
      {children}
    </motion.div>
  );
}

function PortalSlashRatioStat({
  numeratorTarget,
  denominatorTarget,
  embedded,
  testId,
  ariaLabel,
  numeratorGlow = false,
}) {
  const n = useAnimatedCount(numeratorTarget);
  const d = useAnimatedCount(denominatorTarget, { duration: 0.56 });
  const glow = numeratorGlow ? " drop-shadow-[0_0_12px_rgba(0,30,255,0.16)]" : "";
  /* Match .t-btn-primary: 13px, 0.04em tracking, weight 400 */
  const digitBase =
    "font-heading !font-normal tabular-nums text-[13px] leading-none tracking-[0.04em]";
  const numClass = `${digitBase} text-[var(--t-accent)]${glow}`;
  const denClass = `${digitBase} text-[var(--t-text)] opacity-85`;
  const slashClass = `${digitBase} select-none font-heading !font-normal text-[var(--t-meta)] px-0.5`;

  return (
    <StatShell embedded={embedded} data-testid={testId} role="status" aria-label={ariaLabel}>
      <motion.span key={`n-${numeratorTarget}`} className={numClass} layout initial={{ opacity: 0.35, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.28, ease: EASE }}>
        {n}
      </motion.span>
      <span className={slashClass} aria-hidden>
        /
      </span>
      <motion.span key={`d-${denominatorTarget}`} className={denClass} layout initial={{ opacity: 0.35, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.28, ease: EASE, delay: 0.04 }}>
        {d}
      </motion.span>
    </StatShell>
  );
}

/** Folder file count / library total — accent numerator, animated digits. */
export function PortalLibraryRatioStat({ folderCount, totalCount, embedded, testId = "library-count" }) {
  return (
    <PortalSlashRatioStat
      numeratorTarget={folderCount}
      denominatorTarget={totalCount}
      embedded={embedded}
      testId={testId}
      ariaLabel={`${folderCount} files in this folder of ${totalCount} in the library`}
      numeratorGlow={false}
    />
  );
}

/** Inline tally — label with accent numerator (minimal, no pill). */
export function PortalInlineCount({ visible, total, className = "" }) {
  return (
    <span className={`tabular-nums text-xs text-[var(--t-meta)] sm:text-sm ${className}`}>
      <span className="text-[var(--t-accent)]">{visible}</span>/{total}
    </span>
  );
}

/** Notes tally — visible notes / total notes in studio. */
export function PortalNotesRatioStat({ visibleCount, totalCount, embedded, testId = "notes-count" }) {
  return (
    <PortalSlashRatioStat
      numeratorTarget={visibleCount}
      denominatorTarget={totalCount}
      embedded={embedded}
      testId={testId}
      ariaLabel={`${visibleCount} of ${totalCount} notes`}
      numeratorGlow={false}
    />
  );
}

/** Board tally — saved boards / total boards in studio. */
export function PortalBoardRatioStat({ boardCount, totalCount, embedded, testId = "board-count" }) {
  return (
    <PortalSlashRatioStat
      numeratorTarget={boardCount}
      denominatorTarget={totalCount}
      embedded={embedded}
      testId={testId}
      ariaLabel={`${boardCount} of ${totalCount} boards`}
      numeratorGlow={false}
    />
  );
}

/** Resource tally — actual totals only (`0/0` when empty, `n/n` for any count including large libraries). */
export function PortalResourcesRatioStat({ count, embedded, testId = "resources-count" }) {
  const capped = Math.max(0, Number(count) || 0);
  return (
    <PortalSlashRatioStat
      numeratorTarget={capped}
      denominatorTarget={capped}
      embedded={embedded}
      testId={testId}
      ariaLabel={capped === 0 ? "0 resources" : `${capped} resources`}
      numeratorGlow
    />
  );
}
